/* eslint-disable effector/no-getState */
import type { PropsWithChildren } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'

import * as Sentry from '@sentry/react'
import { useUnit } from 'effector-react'
import type { Channel, Options } from 'pusher-js'
import Pusher from 'pusher-js'
import { useQueryClient } from 'react-query'

import { setAiChCustomValue } from '@/models'
import { $account, $isLoggedIn } from '@/models/account.ts'
import { $creatorId, loadCreatorsFx } from '@/models/creators.ts'
import { toast } from '@/models/toasts'
import { sendCustomAppAction } from '@/modules/desktop/DesktopAppProvider.tsx'
import { PusherEvents, PusherListener } from '@/modules/pusher/constants.ts'
import { apiClient } from '@/utils/axios.ts'
import { noop } from '@/utils/misc.ts'

import {
  finishFanNumberingUpdating,
  startFanNumberingUpdating,
} from '../automations/models/fan-numbering'

const PUSHER_APP_KEY = window.__PUSHER_APP_KEY__
const PUSHER_CLUSTER = window.__PUSHER_CLUSTER__
const PUSHER_CUSTOM_HOST = window.__PUSHER_CUSTOM_HOST__
const PUSHER_PORT =
  (window.__PUSHER_PORT__ && parseInt(window.__PUSHER_PORT__)) || 443
const PUSHER_ENCRYPTED = window.__PUSHER_ENCRYPTED__ !== 'false'

const soketiOptions: Partial<Options> =
  PUSHER_CUSTOM_HOST && PUSHER_CUSTOM_HOST !== 'none'
    ? {
        wsHost: PUSHER_CUSTOM_HOST,
        wsPort: PUSHER_PORT,
        forceTLS: PUSHER_ENCRYPTED,
        disableStats: true,
        enabledTransports: ['ws', 'wss'],
      }
    : {}

const pusher = new Pusher(PUSHER_APP_KEY, {
  ...soketiOptions,
  cluster: PUSHER_CLUSTER,
  channelAuthorization: {
    transport: 'ajax',
    endpoint: '/pusher-auth',
    customHandler: (request, callback) => {
      apiClient
        .post('/pusher-auth', {
          socketId: request.socketId,
          channel: request.channelName,
        })
        .then((response) => {
          if (response.status !== 200) {
            throw new Error('Invalid response with status ' + response.status)
          }

          callback(null, response.data)
        })
    },
  },
})

// Error codes that are expected when tab is hidden/inactive
// 1006: WebSocket closed without close frame (network interruption, tab inactive)
// 4201: Pong reply not received (client was inactive)
const IGNORABLE_HIDDEN_TAB_ERROR_CODES = [1006, 4201]
// 4009: Unauthorized within timeout
const UNAUTHORIZED_ERROR_CODE = 4009

const getErrorCode = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null) {
    const errorObject = ('error' in error ? error.error : error) as Record<
      string,
      unknown
    >

    // Pusher error structure can have code in different places
    if (typeof errorObject.code === 'number') return errorObject.code

    if (typeof errorObject.data === 'object' && errorObject.data !== null) {
      const data = errorObject.data as Record<string, unknown>

      if (typeof data.code === 'number') return data.code
    }
  }

  return undefined
}

const shouldSkipPusherErrorLog = (error?: unknown) => {
  // Always skip if offline - can't do anything about internet issues
  if (!navigator.onLine) return true

  if (!error) return false

  const code = getErrorCode(error)

  const isUnauthorized =
    code === UNAUTHORIZED_ERROR_CODE && !$isLoggedIn.getState()
  const isTabHidden =
    document.visibilityState === 'hidden' &&
    IGNORABLE_HIDDEN_TAB_ERROR_CODES.some((c) => c === code)

  return isUnauthorized || isTabHidden
}

pusher.connection.bind('disconnected', () => {
  // Disconnected is expected when offline
  if (!navigator.onLine) return
  if (!$isLoggedIn.getState()) return
  Sentry.captureMessage('Pusher disconnected', {
    level: 'warning',
    tags: { component: 'PusherProvider', type: 'connection' },
  })
})

pusher.connection.bind('failed', () => {
  if (!navigator.onLine) return
  Sentry.captureException(new Error('Pusher connection failed'), {
    tags: { component: 'PusherProvider', type: 'connection' },
  })
})

pusher.connection.bind('unavailable', () => {
  if (!navigator.onLine) return
  Sentry.captureException(new Error('Pusher connection unavailable'), {
    tags: { component: 'PusherProvider', type: 'connection' },
  })
})

pusher.connection.bind('error', (error: unknown) => {
  if (shouldSkipPusherErrorLog(error)) return
  Sentry.captureException(error, {
    tags: { component: 'PusherProvider', type: 'connection' },
  })
})
const channels = new Map<string, Channel>()

const getChannel = (channelName: string) => {
  const existingChannel = channels.get(channelName)

  if (existingChannel) {
    return Promise.resolve(existingChannel as Channel)
  }

  return new Promise<Channel>((resolve, reject) => {
    try {
      const channel = pusher.subscribe(channelName)

      channel.bind('pusher:subscription_succeeded', () => {
        channels.set(channelName, channel)
        resolve(channel)
      })
      channel.bind('pusher:subscription_error', (error?: unknown) => {
        const subscriptionError = new Error(
          `Failed to subscribe to channel: ${channelName}}`,
        )

        Sentry.captureException(subscriptionError, {
          tags: {
            component: 'PusherProvider',
            type: 'subscription',
            channelName,
          },
          extra: { error },
        })
        reject()
      })
    } catch (error) {
      const subscriptionError =
        error instanceof Error
          ? error
          : new Error(`Failed to subscribe to channel: ${channelName}`)

      Sentry.captureException(subscriptionError, {
        tags: {
          component: 'PusherProvider',
          type: 'subscription',
          channelName,
        },
      })
      reject(subscriptionError)
    }
  })
}

type SubscribeToChannelFunction = (
  channel: string,
  listener: PusherListener,
) => () => void

export interface PusherContextProps {
  subscribeToChannel: SubscribeToChannelFunction
}

const PusherContext = createContext<PusherContextProps>({
  subscribeToChannel: () => {
    return noop
  },
})

export const sendToPusherChannel = (
  channelName: string,
  event: string,
  payload: object,
) => {
  getChannel(channelName).then((channel) => {
    channel.trigger(event, payload)
  })
}

export const usePusher = () => {
  return useContext(PusherContext)
}

export const PusherProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient()
  const creatorId = useUnit($creatorId)

  const subscribeToChannel = useCallback(
    (channelName: string, listener: PusherListener) => {
      const channelPromise = getChannel(channelName)

      channelPromise.then((channel) => {
        channel.bind_global(listener)
      })

      return () => {
        channelPromise.then((channel) => {
          channel.unbind_global(listener)
        })
      }
    },
    [],
  )

  const values: PusherContextProps = useMemo(
    () => ({
      subscribeToChannel,
    }),
    [subscribeToChannel],
  )

  const account = useUnit($account)

  // Subscribing to channel
  useEffect(
    () => {
      if (!account?.ownerId) return

      return subscribeToChannel(
        `private-desktop-app-${account.ownerId}`,
        (event, data) => {
          console.log('Desktop App Pusher event', event, data)

          if (
            event === PusherEvents.FAN_NUMBERING_STARTED_PROCESSING &&
            data?.creatorId === creatorId
          ) {
            startFanNumberingUpdating()
          }

          if (
            event === PusherEvents.FAN_NUMBERING_FINISHED_PROCESSING &&
            data?.creatorId === creatorId
          ) {
            finishFanNumberingUpdating()
            toast.success(
              'Fan numbers assigned successfully. Inbox Splitter is now available.',
            )
          }

          if (event === PusherEvents.AICH_SETTINGS_CHANGED) {
            setAiChCustomValue('featureStatus', data.status)
          }

          // Handling for chatting UI
          if (
            event === PusherEvents.CHAT_MESSAGE_COUNTS &&
            Array.isArray(data.counts)
          ) {
            for (const entry of data.counts) {
              queryClient.setQueryData(
                ['unreadChatsCount', entry.creatorId],
                entry.chatMessagesCount,
              )
            }
          }
          // Handling for changed permissions: ignore if userIds don't match,
          // reload creators otherwise (event is sent in the case any permission changed)
          // Event contains now the data (could be used further for some conditions):
          // {
          //   creatorId: number
          //   prevPermissions: Array<CreatorPermission>
          //   newPermissions: Array<CreatorPermission>
          // }
          if (event === PusherEvents.PERMISSIONS_CHANGED && !!data?.userId) {
            if (account.userId !== data.userId) {
              return
            }

            return loadCreatorsFx()
          }

          sendCustomAppAction('pusher-channel-update-received', { event, data })
        },
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subscribeToChannel, account, creatorId],
  )

  return (
    <PusherContext.Provider value={values}>{children}</PusherContext.Provider>
  )
}
